import * as React from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import UserTableRowViewModel from './ViewModels/UserTableRowViewModel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { APIContext } from '../../Contexts/APIContext';
import Tooltip from '@mui/material/Tooltip';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

interface IProps {
    row: UserTableRowViewModel;
    refresh: () => void;
    edit: (id: string) => void;
}

export default function OrgUserManagementRow(props: IProps) {
    const { row, refresh, edit } = props;
    const { get, showMessage } = React.useContext(APIContext);
    const [loading, setLoading] = React.useState(false);
    const [anchorRef, setAnchorRef] = React.useState<HTMLElement | null>(null);
    const open = Boolean(anchorRef);

    const reset = async (event: any) => {
        event.stopPropagation();
        const action = `SendResetPasswordEmail?email=${encodeURIComponent(row.email)}`;
        setLoading(true);
        await get('AdminUser', action, (success: any) => {
            showMessage('success', 'Reset password link sent');
        })
        setLoading(false);
    }

    const confirm = async (event: any) => {
        event.stopPropagation();
        setLoading(true);
        await get("AdminUser", `ResendEmailConfirmation?id=${row.id}`, (success: any) => {
            showMessage('success', 'New confirmation email has been sent');
        });
        setLoading(false);
    }

    const toggleArchive = async (event: any) => {
        event.stopPropagation();
        setLoading(true);
        await get("AdminUser", `UpdateArchiveStatus?id=${row.id}`, (success: any) => {
            showMessage('success', row.archived ? 'User has been re-activated' : 'User has been archived');
        });
        setLoading(false);
        refresh();
    }

    const handleClose = (event: any) => {
        event.stopPropagation();
        setAnchorRef(null);
    };

    const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorRef(event.currentTarget);
    };

    const resetOfficeConfirmation = async (event: any) => {
        event.stopPropagation();
        setLoading(true);
        await get("AdminUser", `ResetOfficeConfirmation?id=${row.id}`, () => {
            showMessage('success', 'Office confirmation has been reset');
        });
        setLoading(false);
        refresh();
    }

    return (
        <TableRow >
            <TableCell>{row.firstName + ' ' + row.lastName}</TableCell>
            <TableCell>{row.email}</TableCell>
            <TableCell>{row.role}</TableCell>
            <TableCell>{row.site}</TableCell>
            <TableCell>
                {row.officeChangePending &&
                    <Tooltip title="Office Change Request Received">
                        <PendingActionsIcon color="info" fontSize="small" aria-label="Office Change Request Received" />
                    </Tooltip>
                }
                {!row.officeChangePending && row.officeConfirmationNeverCompleted &&
                    <Tooltip title="Office Never Confirmed">
                        <HelpOutlineIcon color="disabled" fontSize="small" aria-label="Office Never Confirmed" />
                    </Tooltip>
                }
                {!row.officeChangePending && !row.officeConfirmationNeverCompleted && row.officeConfirmationOverdue &&
                    <Tooltip title="User Has Not Accessed For At Least 12 Months Since Confirmation Expired">
                        <EventBusyIcon color="error" fontSize="small" aria-label="User Has Not Accessed For At Least 12 Months Since Confirmation Expired" />
                    </Tooltip>
                }
                {!row.officeChangePending && !row.officeConfirmationNeverCompleted && !row.officeConfirmationOverdue && row.officeConfirmationDue &&
                    <Tooltip title="Office Confirmation Pending">
                        <ScheduleIcon color="warning" fontSize="small" aria-label="Office Confirmation Pending" />
                    </Tooltip>
                }
                {!row.officeChangePending && !row.officeConfirmationNeverCompleted && !row.officeConfirmationOverdue && !row.officeConfirmationDue &&
                    <Tooltip title="Office Currently Confirmed">
                        <CheckCircleIcon color="success" fontSize="small" aria-label="Office Currently Confirmed" />
                    </Tooltip>
                }
            </TableCell>
            <TableCell align='right'>
                <Grid container spacing={1} justifyContent='flex-end' alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                    <Grid>
                        <IconButton size="small" disabled={loading} onClick={handleToggle} aria-label="Staff actions dropdown">
                            <MoreVertIcon />
                        </IconButton>
                        <Menu
                            open={open}
                            onClose={handleClose}
                            anchorEl={anchorRef}
                        >
                            <MenuItem
                                onClick={() => {
                                    edit(row.id);
                                    setAnchorRef(null);
                                }}
                                disabled={loading}
                            >
                                Edit
                            </MenuItem>
                            <MenuItem
                                onClick={toggleArchive}
                                disabled={loading}
                            >
                                {row.archived ? "Re-activate" : "Archive"}
                            </MenuItem>
                            <MenuItem
                                onClick={resetOfficeConfirmation}
                                disabled={loading}
                            >
                                Reset Confirmation
                            </MenuItem>
                            {row.emailConfirmed &&
                                <MenuItem
                                    onClick={reset}
                                    aria-label='reset password'
                                    disabled={loading}
                                >
                                    Reset Password
                                </MenuItem>
                            }
                            {!row.emailConfirmed &&
                                <MenuItem
                                    onClick={confirm}
                                    aria-label='Send confirmation email'
                                    disabled={loading}
                                >
                                    Re-send Confirmation
                                </MenuItem>
                            }
                        </Menu>
                    </Grid>
                </Grid>
            </TableCell>
        </TableRow>
    );
}
